'use client';

import { useLocale, useTranslations } from 'next-intl';

type Offer = { label: string; title: string; body: string; pills: string[] };

/**
 * "What we do" section: two equal offer cards (AI knowledge / AI solution),
 * each with a label, title, body, and keyword pills, plus two text links.
 * The four-step process is its own section (ProcessFlow / "Der Ablauf").
 */
function OfferCard({ offer }: { offer: Offer }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-white p-8 shadow-[0_6px_22px_-12px_rgba(16,24,40,0.18)]">
      <p className="font-mono text-eyebrow font-semibold tracking-[var(--hero-label-tracking)] uppercase text-font-navy">
        {offer.label}
      </p>
      <h3 className="text-[length:var(--text-feature-title)] font-medium tracking-[-0.01em] leading-tight text-font-secondary">
        {offer.title}
      </h3>
      <p className="text-nav text-font-tertiary font-light leading-relaxed flex-1">
        {offer.body}
      </p>
      <div className="flex flex-wrap gap-2 pt-2">
        {offer.pills.map((pill) => (
          <span
            key={pill}
            className="text-nav font-medium text-font-secondary bg-stats-bg rounded-full px-3.5 py-1.5"
          >
            {pill}
          </span>
        ))}
      </div>
    </div>
  );
}

export function DataTranslation() {
  const t = useTranslations('dataTranslation');
  const locale = useLocale();
  const knowledge = t.raw('card_knowledge') as Offer;
  const solution = t.raw('card_solution') as Offer;

  return (
    <section className="cv-auto bg-stats-bg py-24 px-[10%]">
      <div className="max-w-6xl flex flex-col gap-10">

        {/* Header */}
        <div className="flex flex-col gap-4 max-w-3xl">
          <p className="font-mono text-eyebrow font-semibold tracking-[var(--hero-label-tracking)] uppercase text-font-navy">
            {t('eyebrow')}
          </p>
          <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-secondary">
            {t('block1_title')}
          </h2>
        </div>

        {/* Two offer cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          <OfferCard offer={knowledge} />
          <OfferCard offer={solution} />
        </div>

        <p className="text-subheadline text-font-tertiary font-light leading-relaxed max-w-3xl">
          {t('closer')}
        </p>

        {/* Links */}
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          <a
            href={`/${locale}/work`}
            className="group inline-flex items-center gap-2 text-nav font-medium text-font-navy w-fit"
          >
            {t('block1_link')}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/icons/arrow-right.svg" alt="" className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href={`/${locale}/ai-models`}
            className="group inline-flex items-center gap-2 text-nav font-medium text-font-navy w-fit"
          >
            {t('link_tech')}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/icons/arrow-right.svg" alt="" className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

      </div>
    </section>
  );
}
